import utils from "./utils";
import defaults from "./defaults";
import RequestAuth from "./RequestAuth";

/**
 * Handles authentication processes, including login, token refresh, and logout.
 */
class Authenticate {

  /**
   * Creates an instance of the `Authenticate` class.
   *
   * @param {Tenants} tenants - An instance of the `Tenants` class to manage tenant data.
   */
  constructor(tenants) {
    /**
     * @property {Tenants} tenants - The tenants instance for managing tenant data.
     */
    this.tenants = tenants;

    /**
     * @property {RequestAuth} requestAuth - Handles authentication-related API requests.
     */
    this.requestAuth = new RequestAuth(this.tenants);
  }

  /**
   * Checks if the user is currently authenticated.
   *
   * Authentication is determined by the presence of an `accessToken` in the current tenant.
   *
   * @returns {boolean} `true` if the user is authenticated, otherwise `false`.
   */
  isAuthenticated() {
    return !!this.tenants.getCurrent()?.accessToken;
  }

  /**
   * Authenticates a user with email and password, then issues tokens for the current tenant.
   *
   * @param {string} email - The user's email address.
   * @param {string} password - The user's password.
   * @param {Object} options - Additional options for authentication (currently unused).
   * @returns {Promise<void>} Resolves when authentication is successful.
   * @throws {Error} Logs and handles errors if authentication fails.
   */
  async authenticate(email, password, options) {
    utils.log('Authenticating...');

    try {
      const loginRequestResult = await this.requestAuth.loginRequest(email, password);

      utils.log('loginRequestResult', loginRequestResult);

      const issueTokensResult = await this.requestAuth.issueTokensRequest(
        loginRequestResult.ticket,
        this.tenants.getCurrent().key
      );

      utils.log('issueTokensResult', issueTokensResult);

    } catch (error) {
      utils.errorMessage(error);
      utils.logError(error);
    }

  }

  /**
   * Refreshes authentication tokens for the current tenant.
   *
   * @returns {Promise<Object>} Resolves with the new tokens when successful.
   * @throws {Error} Logs and handles errors if token refresh fails.
   */
  async refreshTokens() {
    return await this.requestAuth.refreshTokensRequest(
      this.tenants.getCurrent().refreshToken
    );
  }

  /**
   * Logs the user out by clearing tenant data and resetting the current tenant index.
   *
   * Updates the defaults with the cleared tenants.
   *
   * @returns {Promise<boolean>} Resolves with `true` when logout is complete.
   */
  async logout() {
    this.tenants.set([]);
    this.tenants.setCurrentIndex(0);

    // TODO: logout on remote

    defaults.update(this.tenants);

    return true;
  }

}

export default Authenticate;